import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useUserStore } from "store";
import { getCsrfToken } from "../../utils";
import { formatPriceWithCurrency } from "../../utils/vat";
import MessagePreviewDialog from "../shared/MessagePreviewDialog";

// "Osamyynti" sub-tab of the Ominaisuudet (Features) settings tab.
// Rendered for eligible orgs (counter_sales_eligible gate in OrganizationFeaturesTab):
// Kassamyynti-only (Perus+) and full Osamyynti (Pro+). Holds the shared feature
// toggle plus the parts-sales-specific options — notably the arrival SMS, which is
// billed per message and therefore an explicit opt-in with its price shown next to
// the switch. The SMS option belongs to lähete special orders, so it is hidden for
// Kassamyynti-only orgs.
function FeaturesPartsSalesTab({ t, onDirtyChange }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const fullOsamyynti = !!currentOrganization?.parts_sales_eligible;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [smsUnitPrice, setSmsUnitPrice] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!currentOrganization) return;
    setForm({
      parts_sales_enabled: currentOrganization.parts_sales_enabled || false,
      parts_sales_arrival_sms_enabled: currentOrganization.parts_sales_arrival_sms_enabled || false,
    });
    setIsDirty(false);
  }, [currentOrganization?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // SMS unit price from the billing usage endpoint (same source the billing tab
  // shows). Purely informational — if the fetch fails, the toggle still works.
  useEffect(() => {
    let cancelled = false;
    axios
      .get("/api/payments/usage/")
      .then((res) => {
        if (!cancelled) setSmsUnitPrice(res.data?.pricing?.sms_sent?.unit_price ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.organization_id]);

  const handleToggle = (field) => (event) => {
    const checked = event.target.checked;
    setForm((prev) => ({ ...prev, [field]: checked }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      setSaving(true);
      await axios.patch(
        `/api/organizations/${currentOrganization.organization_id}/`,
        {
          parts_sales_enabled: form.parts_sales_enabled,
          parts_sales_arrival_sms_enabled: form.parts_sales_arrival_sms_enabled,
        },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      // Refresh user store so nav menu and feature gates update immediately
      await fetchUser();
      setIsDirty(false);
      setSnackbar({
        open: true,
        message: t("Organization updated successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update parts sales settings:", error);
      const message = error.response?.data?.detail || t("Failed to update organization");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Report dirty state to parent (sub-tab + tab-switch guards).
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const smsHelp = smsUnitPrice != null
    ? t("parts_sales.arrivalSmsHelpWithPrice", { price: formatPriceWithCurrency(smsUnitPrice) })
    : t("parts_sales.arrivalSmsHelp");

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.parts_sales_enabled}
                onChange={handleToggle("parts_sales_enabled")}
                data-testid="parts-sales-toggle"
              />
            }
            label={t("parts_sales.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t(fullOsamyynti ? "parts_sales.featureToggleHelp" : "parts_sales.featureToggleHelpCounterOnly")}
          </Typography>
        </Grid>

        {fullOsamyynti && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.parts_sales_arrival_sms_enabled}
                  onChange={handleToggle("parts_sales_arrival_sms_enabled")}
                  disabled={!form.parts_sales_enabled}
                  data-testid="parts-sales-arrival-sms-toggle"
                />
              }
              label={t("parts_sales.arrivalSmsToggle")}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
              {smsHelp}
            </Typography>
            <Button
              size="small"
              onClick={() => setPreviewOpen(true)}
              sx={{ ml: 6, mt: 0.5 }}
              data-testid="parts-sales-arrival-sms-preview-button"
            >
              {t("parts_sales.arrivalSmsPreview")}
            </Button>
            <MessagePreviewDialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              title={t("parts_sales.arrivalSmsPreviewTitle")}
              defaultLanguage={currentOrganization?.default_language}
              loadPreview={async (language) => {
                const res = await axios.post(
                  `/api/organizations/${currentOrganization.organization_id}/message-preview/`,
                  { kind: "parts_arrival_sms", draft: {}, language },
                  { headers: { "X-Organization-Id": currentOrganization.organization_id } },
                );
                return res.data;
              }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || !isDirty}
              data-testid="parts-sales-settings-save"
            >
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(FeaturesPartsSalesTab);
